/**
 * TanStack Query hooks — Brokers.
 *
 * 8.B avait livré le minimum nécessaire au BrokerPicker (lookup courte
 * liste + lecture par id). 8.C complète :
 *
 *   - useBrokers({cursor, q, role, include_disabled}) → liste paginée
 *     pour la page /brokers.
 *   - useBrokerWithProfile(id) → couple {user, profile} pour la page
 *     détail /brokers/:id (le profile contient license_number,
 *     license_expiry, specialties, bio, signature_block).
 *
 * On garde les hooks 8.B (`useBrokersForPicker`, `useBroker`) intacts pour
 * ne pas casser BrokerPicker. `useBroker` extrait .user uniquement, c'est
 * suffisant pour l'affichage compact du picker. Pour la page détail on
 * a besoin du profile aussi → hook séparé.
 *
 * Convention queryKey :
 *   ['brokers', 'picker', {q, role}]    — picker (8.B)
 *   ['brokers', 'list', {cursor, q, role, include_disabled}]  — page liste (8.C)
 *   ['brokers', 'detail', id]           — picker readback (8.B)
 *   ['brokers', 'detail-full', id]      — page détail user+profile (8.C)
 */

import type { BrokerRow, DbPage } from "@kollectivia/shared";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";

export type Broker = BrokerRow;
export type BrokerListResult = DbPage<BrokerRow>;

// -----------------------------------------------------------------------------
// 8.B — picker (inchangé)
// -----------------------------------------------------------------------------

export type BrokerPickerParams = {
  q?: string;
  /** "broker" par défaut (pas tenant_admin). */
  role?: "broker" | "tenant_admin" | "all";
};

function buildPickerPath(params: BrokerPickerParams): string {
  const sp = new URLSearchParams();
  sp.set("role", params.role ?? "broker");
  sp.set("include_disabled", "false");
  if (params.q) sp.set("q", params.q);
  sp.set("limit", "20");
  return `/v1/brokers?${sp.toString()}`;
}

export function useBrokersForPicker(params: BrokerPickerParams = {}) {
  return useQuery({
    queryKey: ["brokers", "picker", params],
    queryFn: () => api.get<BrokerListResult>(buildPickerPath(params)),
    placeholderData: (prev) => prev,
  });
}

export function useBroker(id: string | undefined) {
  return useQuery({
    queryKey: ["brokers", "detail", id],
    // 9.BB.1 — la forme réelle du wire est PLATE.
    //
    // Ce hook lisait `resp.user`, mais `GET /v1/brokers/:id` renvoie
    // `{ ok, data: { ...colonnes user, profile } }` et `api.get` déverse déjà
    // `data`. `resp.user` valait donc `undefined`, et le picker — qui n'affiche
    // sa carte « sélectionné » que si `value && selected.data` — restait sur son
    // champ de recherche : cliquer un conseiller ne paraissait rien faire. Le
    // hook voisin `useBrokerWithProfile` documentait pourtant la bonne forme.
    // Le picker n'a besoin que des colonnes utilisateur ; `profile` est ignoré.
    queryFn: () => api.get<BrokerRow>(`/v1/brokers/${id}`),
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------------
// 8.C — liste paginée + détail user+profile
// -----------------------------------------------------------------------------

/**
 * Profil métier broker — colonnes de `app.broker_profiles` exposées par
 * GET /v1/brokers/:id (USER_COLS + .profile = PROFILE_COLS).
 *
 * Note : pas typé dans @kollectivia/shared/db-rows.ts en 8.B (on n'avait
 * pas besoin du profile). On déclare ici localement plutôt que de toucher
 * shared — décision 8.C : zéro modif shared/api. 8.D n'a pas eu besoin
 * de ce type (la fiche garantie ne touche pas aux brokers). Si un futur
 * chunk d'édition broker (PATCH /v1/brokers/:id) apparaît, on extraira
 * en BrokerProfileRow shared à ce moment-là.
 */
export type BrokerProfile = {
  user_id: string;
  license_number: string | null;
  license_expiry: string | null;
  specialties: string[] | null;
  bio: string | null;
  signature_block: string | null;
  created_at: string;
  updated_at: string;
};

export type BrokerWithProfile = BrokerRow & {
  profile: BrokerProfile | null;
};

export type BrokerListParams = {
  cursor?: string | null;
  limit?: number;
  q?: string;
  role?: "broker" | "tenant_admin" | "all";
  include_disabled?: boolean;
};

function buildListPath(params: BrokerListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.q) sp.set("q", params.q);
  // "all" est le default backend ; on ne le pousse que s'il diverge.
  if (params.role && params.role !== "all") sp.set("role", params.role);
  if (params.include_disabled) sp.set("include_disabled", "true");
  const qs = sp.toString();
  return qs ? `/v1/brokers?${qs}` : "/v1/brokers";
}

export function useBrokers(params: BrokerListParams) {
  return useQuery({
    queryKey: ["brokers", "list", params],
    queryFn: () => api.get<BrokerListResult>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

/**
 * Détail broker complet — user + broker_profile.
 * Backend : GET /v1/brokers/:id renvoie { ok, data: { ...userCols, profile } }.
 * On a `api.get<T>` qui unwrap déjà `data` (cf. lib/api.ts), donc T =
 * BrokerWithProfile directement.
 */
export function useBrokerWithProfile(id: string | undefined) {
  return useQuery({
    queryKey: ["brokers", "detail-full", id],
    queryFn: () => api.get<BrokerWithProfile>(`/v1/brokers/${id}`),
    enabled: !!id,
  });
}
